import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Box, Typography, Grid, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, LinearProgress,
} from "@mui/material";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from "recharts";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BarChartIcon from "@mui/icons-material/BarChart";
import PaymentsIcon from "@mui/icons-material/Payments";
import api from "../../../services/api";
import StatsCard from "../../../components/admin/StatsCard";
import LoadingSkeleton from "../../../components/common/LoadingSkeleton";
import EmptyState from "../../../components/admin/EmptyState";

const PIE_COLORS = ["#f39c12", "#2a9ac6", "#1e88e5", "#8e44ad", "#27ae60", "#e74c3c", "#95a5a6"];
const formatPrice = (v) => v != null ? `${v.toLocaleString("ko-KR")}원` : "0원";
const toSafeNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};
const toSafeText = (value, fallback = "-") =>
  typeof value === "string" && value.trim() ? value.trim() : fallback;

function buildRequestUrl(params = {}) {
  const search = new URLSearchParams({ fromDate: "", toDate: "", ...params });
  return `/admin/dream/requests?${search.toString()}`;
}

function StatLink({ to, children }) {
  return (
    <Box
      component={Link}
      to={to}
      sx={{
        display: "block",
        color: "inherit",
        textDecoration: "none",
        height: "100%",
        "& .MuiCard-root": {
          height: "100%",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
        },
        "&:hover .MuiCard-root": {
          transform: "translateY(-2px)",
          boxShadow: "0 10px 24px rgba(0,0,0,0.12)",
        },
      }}
    >
      {children}
    </Box>
  );
}

export default function MarketStatsTab() {
  const [stats, setStats] = useState(null);
  const [perf, setPerf] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/api/market-locations/stats/dream"),
      api.get("/api/market-locations/collector-performance"),
    ])
      .then(([s, p]) => { setStats(s.data); setPerf(p.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSkeleton variant="chart" />;
  if (!stats) return null;

  const price = stats.priceSummary || {};
  const autoExposure = stats.autoExposureSummary || {};
  const pieData = [
    { name: "대기", value: toSafeNumber(stats.statusCounts?.PENDING) },
    { name: "배정됨", value: toSafeNumber(stats.statusCounts?.ASSIGNED) },
    { name: "이동 중", value: toSafeNumber(stats.statusCounts?.ON_THE_WAY) },
    { name: "승인 대기", value: toSafeNumber(stats.statusCounts?.AWAITING_APPROVAL) },
    { name: "유효 완료", value: toSafeNumber(stats.validCompletedCount ?? stats.statusCounts?.COMPLETED) },
    { name: "무효 완료", value: toSafeNumber(stats.voidedCompletedCount) },
    { name: "취소", value: toSafeNumber(stats.statusCounts?.CANCELLED) },
  ].filter((d) => d.value > 0);
  const monthlyTrend = Array.isArray(stats.monthlyTrend)
    ? stats.monthlyTrend
        .filter((item) => item?.period)
        .map((item) => ({
          period: String(item.period),
          count: toSafeNumber(item.count),
        }))
    : [];
  const dailyTrend = Array.isArray(stats.dailyTrend)
    ? stats.dailyTrend
        .filter((item) => item?.date)
        .map((item) => ({
          date: String(item.date),
          count: toSafeNumber(item.count),
        }))
    : [];
  const collectorNameCounts = (Array.isArray(perf) ? perf : []).reduce((map, row) => {
    const name = toSafeText(row?.collectorName, "이름 없음");
    map.set(name, (map.get(name) || 0) + 1);
    return map;
  }, new Map());
  const safePerf = (Array.isArray(perf) ? perf : []).map((row, index) => {
    const collectorId = row?.collectorId ?? `unknown-${index + 1}`;
    const collectorName = toSafeText(row?.collectorName, "이름 없음");
    const hasDuplicateName = (collectorNameCounts.get(collectorName) || 0) > 1;
    return {
      collectorId,
      collectorName,
      chartCollectorName: hasDuplicateName ? `${collectorName} (${collectorId})` : collectorName,
      totalAssigned: toSafeNumber(row?.totalAssigned),
      completedCount: toSafeNumber(row?.completedCount),
      voidedCompletedCount: toSafeNumber(row?.voidedCompletedCount),
      pendingCount: Math.max(0, toSafeNumber(row?.pendingCount)),
      completionRate: Math.min(100, Math.max(0, toSafeNumber(row?.completionRate))),
      totalRevenue: toSafeNumber(row?.totalRevenue),
    };
  });
  const regionalSummary = Array.isArray(stats.regionalRequestSummary)
    ? stats.regionalRequestSummary.map((row) => ({
        region: toSafeText(row?.region, "지역 미지정"),
        count: toSafeNumber(row?.count),
      }))
    : [];
  const timeSlotSummary = Array.isArray(stats.timeSlotRequestSummary)
    ? stats.timeSlotRequestSummary.map((row) => ({
        timeSlot: toSafeText(row?.timeSlot, "미지정"),
        count: toSafeNumber(row?.count),
      }))
    : [];
  const itemSummary = Array.isArray(stats.itemRequestSummary)
    ? stats.itemRequestSummary.map((row) => ({
        itemId: row?.itemId,
        itemName: toSafeText(row?.itemName, "품목 미지정"),
        category: toSafeText(row?.category, "-"),
        quantity: toSafeNumber(row?.quantity),
        amount: toSafeNumber(row?.amount),
      }))
    : [];
  const unassignedRegionSummary = Array.isArray(stats.unassignedRegionSummary)
    ? stats.unassignedRegionSummary.map((row) => ({
        region: toSafeText(row?.region, "지역 미지정"),
        pendingCount: toSafeNumber(row?.pendingCount),
        overdueCount: toSafeNumber(row?.overdueCount),
      }))
    : [];
  const autoExposureFailureReasons = Array.isArray(autoExposure.failureReasons)
    ? autoExposure.failureReasons.map((row) => ({
        reason: toSafeText(row?.reason, "사유 미확인"),
        count: toSafeNumber(row?.count),
      }))
    : [];
  const exportItemSummary = Array.isArray(stats.exportItemSummary)
    ? stats.exportItemSummary.map((row) => ({
        itemCode: toSafeText(row?.itemCode, "UNKNOWN"),
        itemName: toSafeText(row?.itemName, "품목 미지정"),
        quantity: toSafeNumber(row?.quantity),
        exportAvailableQuantity: toSafeNumber(row?.exportAvailableQuantity),
        exceptionQuantity: toSafeNumber(row?.exceptionQuantity),
      }))
    : [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* 요약 카드 */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatLink to={buildRequestUrl()}><StatsCard title="총 수거 요청" value={`${stats.totalRequests ?? 0}건`} icon={StorefrontIcon} color="#2a9ac6" /></StatLink>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatLink to={buildRequestUrl({ status: "PENDING" })}><StatsCard title="대기 중" value={`${stats.statusCounts?.PENDING ?? 0}건`} icon={PendingActionsIcon} color="#f39c12" /></StatLink>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatLink to={buildRequestUrl({ status: "ASSIGNED" })}><StatsCard title="배정됨" value={`${stats.statusCounts?.ASSIGNED ?? 0}건`} icon={PersonAddIcon} color="#2a9ac6" /></StatLink>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard title="이동/검수 중" value={`${(stats.statusCounts?.ON_THE_WAY ?? 0) + (stats.statusCounts?.AWAITING_APPROVAL ?? 0)}건`} icon={AssignmentTurnedInIcon} color="#1e88e5" />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatLink to={buildRequestUrl({ status: "COMPLETED" })}><StatsCard title="유효 완료" value={`${stats.validCompletedCount ?? stats.statusCounts?.COMPLETED ?? 0}건`} icon={AssignmentTurnedInIcon} color="#27ae60" /></StatLink>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatsCard title="무효 완료" value={`${stats.voidedCompletedCount ?? 0}건`} icon={AssignmentTurnedInIcon} color="#e74c3c" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatsCard title="유효 완료율" value={`${stats.completionRate ?? 0}%`} icon={BarChartIcon} color="#8e44ad" />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatsCard title="자동 노출 실패율" value={`${autoExposure.failureRate ?? 0}%`} icon={PendingActionsIcon} color="#d35400" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatLink to={buildRequestUrl({ status: "PENDING", candidateState: "FAILED" })}><StatsCard title="자동 노출 실패" value={`${autoExposure.failedCount ?? 0}건`} icon={PendingActionsIcon} color="#e67e22" /></StatLink>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatLink to={buildRequestUrl({ status: "PENDING", candidateState: "OFFERED" })}><StatsCard title="수락 대기 노출" value={`${autoExposure.offeredCount ?? 0}건`} icon={AssignmentTurnedInIcon} color="#16a085" /></StatLink>
        </Grid>
      </Grid>

      {/* 금액 카드 */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatLink to={buildRequestUrl()}><StatsCard title="총 예상 금액" value={formatPrice(price.total)} icon={PaymentsIcon} color="#8884d8" /></StatLink>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatLink to={buildRequestUrl({ status: "COMPLETED" })}><StatsCard title="완료 금액" value={formatPrice(price.completed)} icon={PaymentsIcon} color="#27ae60" /></StatLink>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatLink to={buildRequestUrl({ status: "ASSIGNED" })}><StatsCard title="배정 금액" value={formatPrice(price.assigned)} icon={PaymentsIcon} color="#2a9ac6" /></StatLink>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatLink to={buildRequestUrl({ status: "PENDING" })}><StatsCard title="대기 금액" value={formatPrice(price.pending)} icon={PaymentsIcon} color="#f39c12" /></StatLink>
        </Grid>
      </Grid>

      {/* 차트 */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card><CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>상태별 비율</Typography>
            <Box sx={{ height: 280 }}>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={80} innerRadius={40}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {PIE_COLORS.map((c, i) => <Cell key={i} fill={c} />)}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <EmptyChart />}
            </Box>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card><CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>월별 수거 요청 추이</Typography>
            <Box sx={{ height: 280 }}>
              {monthlyTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="period" tick={{ fontSize: 12 }}
                      tickFormatter={(v) => {
                        const value = String(v ?? "");
                        const p = value.split("-");
                        return p.length >= 2 ? `${p[0].slice(2)}/${p[1]}` : value;
                      }} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <RechartsTooltip />
                    <Line type="monotone" dataKey="count" name="요청 건수" stroke="#2a9ac6" strokeWidth={3} dot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <EmptyChart />}
            </Box>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Card><CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>일별 수거 요청 (희망 날짜 기준)</Typography>
            <Box sx={{ height: 280 }}>
              {dailyTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => String(v ?? "").slice(5)} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <RechartsTooltip />
                    <Bar dataKey="count" name="요청 건수" fill="#27ae60" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <EmptyChart />}
            </Box>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* 운영 판단 지표 */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card><CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>지역별 요청량</Typography>
            {regionalSummary.length === 0 ? <EmptyState message="지역별 요청 데이터가 없습니다." /> : (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow><TableCell>지역</TableCell><TableCell align="right">요청</TableCell></TableRow></TableHead>
                  <TableBody>{regionalSummary.slice(0, 10).map((row) => (
                    <TableRow key={row.region} hover>
                      <TableCell>{row.region}</TableCell>
                      <TableCell align="right">{row.count}건</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card><CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>시간대별 요청량</Typography>
            {timeSlotSummary.length === 0 ? <EmptyState message="시간대별 요청 데이터가 없습니다." /> : (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow><TableCell>시간대</TableCell><TableCell align="right">요청</TableCell></TableRow></TableHead>
                  <TableBody>{timeSlotSummary.map((row) => (
                    <TableRow key={row.timeSlot} hover>
                      <TableCell>{row.timeSlot}</TableCell>
                      <TableCell align="right">{row.count}건</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card><CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>품목별 요청/금액</Typography>
            {itemSummary.length === 0 ? <EmptyState message="품목별 요청 데이터가 없습니다." /> : (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow><TableCell>품목</TableCell><TableCell>카테고리</TableCell><TableCell align="right">수량</TableCell><TableCell align="right">금액</TableCell></TableRow></TableHead>
                  <TableBody>{itemSummary.slice(0, 10).map((row) => (
                    <TableRow key={row.itemId || row.itemName} hover>
                      <TableCell>{row.itemName}</TableCell>
                      <TableCell>{row.category}</TableCell>
                      <TableCell align="right">{row.quantity}</TableCell>
                      <TableCell align="right">{formatPrice(row.amount)}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card><CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>미배정 발생 지역</Typography>
            {unassignedRegionSummary.length === 0 ? <EmptyState message="미배정 지역 데이터가 없습니다." /> : (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow><TableCell>지역</TableCell><TableCell align="right">미배정</TableCell><TableCell align="right">기한 경과</TableCell></TableRow></TableHead>
                  <TableBody>{unassignedRegionSummary.slice(0, 10).map((row) => (
                    <TableRow key={row.region} hover>
                      <TableCell>{row.region}</TableCell>
                      <TableCell align="right">{row.pendingCount}건</TableCell>
                      <TableCell align="right">{row.overdueCount}건</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card><CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>자동 노출 실패 사유</Typography>
            {autoExposureFailureReasons.length === 0 ? <EmptyState message="자동 노출 실패 데이터가 없습니다." /> : (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow><TableCell>사유</TableCell><TableCell align="right">건수</TableCell></TableRow></TableHead>
                  <TableBody>{autoExposureFailureReasons.map((row) => (
                    <TableRow key={row.reason} hover>
                      <TableCell>{row.reason}</TableCell>
                      <TableCell align="right">{row.count}건</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card><CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>수출 품목 집계</Typography>
            {exportItemSummary.length === 0 ? <EmptyState message="수출 품목 집계 데이터가 없습니다." /> : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>코드</TableCell>
                      <TableCell>품목</TableCell>
                      <TableCell align="right">총 수량</TableCell>
                      <TableCell align="right">수출 가능</TableCell>
                      <TableCell align="right">예외</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>{exportItemSummary.slice(0, 10).map((row) => (
                    <TableRow key={row.itemCode} hover>
                      <TableCell>{row.itemCode}</TableCell>
                      <TableCell>{row.itemName}</TableCell>
                      <TableCell align="right">{row.quantity}</TableCell>
                      <TableCell align="right">{row.exportAvailableQuantity}</TableCell>
                      <TableCell align="right">{row.exceptionQuantity}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* 수거자 성과 */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <BarChartIcon color="primary" /> 수거자 성과
          </Typography>
          {safePerf.length === 0 ? (
            <EmptyState message="수거자 성과 데이터가 없습니다." />
          ) : (
            <>
              <Box sx={{ height: 280, mb: 3 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={safePerf} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis dataKey="chartCollectorName" type="category" tick={{ fontSize: 12 }} width={120} />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="completedCount" name="완료" stackId="a" fill="#27ae60" />
                    <Bar dataKey="voidedCompletedCount" name="무효 완료" stackId="a" fill="#e74c3c" />
                    <Bar dataKey="pendingCount" name="대기" stackId="a" fill="#f39c12" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>수거자</TableCell>
                      <TableCell align="right">총 배정</TableCell>
                      <TableCell align="right">완료</TableCell>
                      <TableCell align="right">무효 완료</TableCell>
                      <TableCell align="right">대기</TableCell>
                      <TableCell align="right">완료율</TableCell>
                      <TableCell align="right">수거 금액</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>진행률</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {safePerf.map((p) => (
                      <TableRow key={p.collectorId} hover>
                        <TableCell><Chip label={p.collectorName} size="small" color="info" variant="outlined" /></TableCell>
                        <TableCell align="right">{p.totalAssigned}</TableCell>
                        <TableCell align="right">{p.completedCount}</TableCell>
                        <TableCell align="right">{p.voidedCompletedCount}</TableCell>
                        <TableCell align="right">{p.pendingCount}</TableCell>
                        <TableCell align="right">{p.completionRate}%</TableCell>
                        <TableCell align="right">{formatPrice(p.totalRevenue)}</TableCell>
                        <TableCell>
                          <LinearProgress variant="determinate" value={p.completionRate} sx={{ height: 8, borderRadius: 4 }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

function EmptyChart() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <Typography color="text.secondary">데이터가 없습니다.</Typography>
    </Box>
  );
}
